import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import { updateMessages } from 'apps/auth/actions/appAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { TableRow } from 'apps/auth/components/common/components/TableRow/TableRow';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { Row } from 'apps/auth/components/common/Layout/Row';
import ComplexPopup from 'apps/auth/components/common/Popup/ComplexPopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import Checkbox from 'apps/auth/components/form/Checkbox';
import Form from 'apps/auth/components/form-multi/Form';
import { PLUS_CONFIRM_SUBSCRIPTION_CLICKED, PLUS_PAYMENT_SUMMARY_SCREEN_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PLUS_SUBSCRIBE_FORM_NAME } from 'apps/auth/constants/formName';
import { URL_IMAGES_PAYSEND_PLUS } from 'apps/auth/constants/UrlConstants';
import { addZero, getDayMonthDate, resolveArguments } from 'apps/auth/helpers/utils';
import { useExtra, useForm, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';

import { GA_POCKET_TO_SUBSCRIPTION, CARD_TO_SUBSCRIPTION, ONE_MONTH_SUBSCRIPTION } from '../utils';

const Logo = styled.img`
    max-width: 100%;
`;

const Checkmark = styled.div`
    line-height: 1;
    margin-right: 18px;
`;

const PaysendPlusPrecheckPopup = (props) => {
    const [isChecked, setIsChecked] = useState(false);
    const {
        plusPrecheckTitle,
        plusPrecheckRenewSubscriptionTitle,
        month,
        threeMonths,
        paymentMethod,
        nextPaymentDate,
        plusPrecheckTerms,
        plusPrecheckButton,
        plusPrecheckCard,
        plusPrecheckAccount,
        defaultBankName,
    } = usePageMessages();
    const { lang } = useLanguage();
    const { subscriptionPaymentInfo } = useExtra();
    const { fields } = useForm(PLUS_SUBSCRIBE_FORM_NAME);
    const isDisabled = !isChecked;
    const method = fields.methodId;
    const pMethod = fields.pMethodId;
    const formattedNextPaymentDate = getDayMonthDate(subscriptionPaymentInfo?.extra?.plus__payment_next_date, lang, 'short');
    useEffect(() => {
        amplitude.getInstance().logEvent(PLUS_PAYMENT_SUMMARY_SCREEN_SHOWN);
    }, []);

    const onSubmitHandler = (fields, json) => {
        props.popupRef.current?.close();
        if (json && json.messageBodyMessages) {
            props.updateMessages(json.messageBodyMessages);
        }
        if (json && json.redirectTo) {
            window.location.replace(json.redirectTo);
        }
    };
    const getNextPaymentDate = (tariff = { id: ONE_MONTH_SUBSCRIPTION }) => {
        const today = new Date();
        const nextPaymentMonth = today.getMonth() + (tariff.id === ONE_MONTH_SUBSCRIPTION ? 1 : 3);
        const newDate = new Date(today.setMonth(nextPaymentMonth));
        return `${addZero(newDate.getDate())} ${newDate.toLocaleString(lang, { month: 'long' })}`;
    };

    const checkHandler = () => {
        setIsChecked(!isChecked);
    };

    const getPaymentMethodName = () => {
        if (pMethod?.value === GA_POCKET_TO_SUBSCRIPTION) {
            return plusPrecheckAccount;
        } else if (pMethod?.value === CARD_TO_SUBSCRIPTION) {
            return `${props.selectedCard?.bankName == null ? defaultBankName : props.selectedCard?.bankName + ' ' + plusPrecheckCard} ·· ${
                props.actionType === 'subscription' ? props.selectedCard?.description : props.selectedCard?.pan
            }`;
        }
    };

    const getRenewPaymentMethodName = () => {
        if (method?.value === GA_POCKET_TO_SUBSCRIPTION) {
            return plusPrecheckAccount;
        } else if (method?.value === CARD_TO_SUBSCRIPTION) {
            return `${props.selectedCard?.bankName == null ? defaultBankName : props.selectedCard?.bankName + ' ' + plusPrecheckCard} ·· ${
                props.actionType === 'subscription' ? props.selectedCard?.description : props.selectedCard?.pan
            }`;
        }
    };

    return (
        <Form
            id="subscription_form"
            action={props.action}
            formName={PLUS_SUBSCRIBE_FORM_NAME}
            validation={false}
            fields={fields}
            validateForm={false}
            getCanonicalPath={false}
            onSubmitHandler={onSubmitHandler}
        >
            <ComplexPopup
                primaryButtonType={'submit'}
                primaryButtonText={plusPrecheckButton}
                primaryButtonDisabled={isDisabled}
                primaryButtonForm={PLUS_SUBSCRIBE_FORM_NAME}
                popupRef={props.popupRef}
                onPrimaryButtonClick={() => {
                    amplitude.getInstance().logEvent(PLUS_CONFIRM_SUBSCRIPTION_CLICKED);
                    props.submitRef.current?.click();
                    props.popupRef.current?.close();
                }}
            >
                <Row indent="adaptive-32-48" textAlign={'center'}>
                    <Logo src={`${URL_IMAGES_PAYSEND_PLUS}/paysend-plus-logo.svg`} alt="plus-logo" />
                </Row>

                <Text color={'primary'} size={'main'} variant={'paragraph'} textAlign={'center'}>
                    {props.actionType === 'subscription' ? plusPrecheckTitle : resolveArguments(plusPrecheckRenewSubscriptionTitle, formattedNextPaymentDate)}
                </Text>

                <Text size={'header'} textAlign={'center'} variant={'div'} indent={'adaptive-24-32'}>
                    {`${props.tariff.amount} ${props.tariff.currencyCode} / ${props.tariff.id === ONE_MONTH_SUBSCRIPTION ? month : threeMonths}`}
                </Text>

                <Row indent={'adaptive-24-32'}>
                    <TableRow title={paymentMethod} pseudoTable>
                        {props.actionType === 'subscription' ? getPaymentMethodName() : getRenewPaymentMethodName()}
                    </TableRow>
                    {props.actionType !== 'subscription' && (
                        <TableRow title={nextPaymentDate} pseudoTable>
                            {getDayMonthDate(subscriptionPaymentInfo?.extra?.plus__payment_next_date, lang, 'short')}
                        </TableRow>
                    )}
                    {props.actionType === 'subscription' && (
                        <TableRow title={nextPaymentDate} pseudoTable>
                            {getNextPaymentDate(props.tariff)}
                        </TableRow>
                    )}
                </Row>

                <Row flex align={'center'}>
                    <Checkmark>
                        <Checkbox onChange={checkHandler} noValidation />
                    </Checkmark>
                    <Text color={'secondary'} size={'secondary'} variant={'div'}>
                        <DangerouslyHtml dangerouslyHtml={plusPrecheckTerms} />
                    </Text>
                </Row>
            </ComplexPopup>
            <Button type="submit" variant={'hidden'} ref={props.submitRef} />
        </Form>
    );
};

export default connect(null, { updateMessages })(PaysendPlusPrecheckPopup);
